import type { ProcedureGroup } from '~/config/referralReasons';

import type { Doctor } from './doctor';

/** Company offices listed on multi-location embeds (address used for full-address display). */
export interface EmbedAvailableOffice {
  id: string;
  name: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  ste?: string | null;
  phone?: string | null;
}

/**
 * Context object for embedded referral forms
 * Contains information about the embedding office and configuration
 */
export interface EmbedContext {
  /** Whether this is an embedded form */
  isEmbedded: boolean;
  /** Authenticated admin preview (?preview=1); not a public/live embed */
  isEmbedPreview?: boolean;
  /** Array of specialties allowed for this embedded form */
  allowedSpecialties?: string[];
  /**
   * Least-recently-assigned doctor id per specialty, for specialties with 2+ matching
   * doctors at this office. Only a recommendation — the actual receivingDoctorId submitted
   * is whatever the form ends up with. See #1376.
   */
  recommendedDoctorBySpecialty?: Record<string, string>;
  /** When non-empty, only these referral reason values are shown in the embed form */
  allowedReasonValues?: string[];
  /** Default doctor pre-selected for referrals */
  defaultDoctor?: Doctor;
  /** List of doctors available for selection in the embed form */
  availableDoctors?: Doctor[];
  /** ID of the office that owns this embedded form */
  officeId?: string;
  /** Name of the office for display purposes */
  officeName?: string;
  /** Company name when the embed host office belongs to a company (e.g. page subtitle) */
  companyName?: string;
  /** ZIP code of the office */
  officeZip?: string;
  /** Full address/phone of the single office hosting this embed (non-multi-office case) — mirrors EmbedAvailableOffice so the pre-selected doctor card can show the same formatted address as the multi-office grid cards. */
  office?: EmbedAvailableOffice;
  /** Unique key for authenticating embedded form submissions */
  embeddedFormKey?: string;
  /** URL of the parent page containing the embedded form */
  parentUrl?: string;
  /** Path to the company logo for branding purposes */
  logoPath?: string;
  /** Insurance carriers accepted by the default doctor with their status */
  insuranceCarriers?: Array<{
    id: string;
    name: string;
    logoUrl: string;
    insuranceStatus: 'IN_NETWORK' | 'OUT_OF_NETWORK' | 'FEE_FOR_SERVICE';
  }>;
  /** Special note to display at the top of the embed form */
  embedNote?: string;
  /** When true, staff must re-enter selected tooth numbers in a text field to verify (reduces entry errors) */
  requireToothVerification?: boolean;
  /** When false, the Referral Status dropdown is hidden in the embed form. Default: true. */
  showReferralStatus?: boolean;
  /** When false, the Chief Complaint textarea is hidden in the embed form. Default: true. */
  showChiefComplaint?: boolean;
  /** When false, the runtime Hide/Show Adult Chart button is removed; chart is always visible. Default: true. */
  showAdultChartToggle?: boolean;
  /** When true, the embed form labels the reasons section as "Procedures"/"Procedure" instead of "Reasons"/"Reason". */
  useProceduresLabel?: boolean;
  /** When true (and the embed form is limited to one specialty), the reasons section renders as a flat clickable list. */
  flatReasonList?: boolean;
  /** When true, the embed form renders the Patient Insurance section at the bottom of step 3 instead of inside step 2. */
  patientInsuranceOnLastStep?: boolean;
  /** When true, show the membership plans link (requires published company plans; opt-in, default off) */
  hasMembershipPlans?: boolean;
  /** Company-wide: offices available for selection (when multi-office) */
  availableOffices?: EmbedAvailableOffice[];
  /** Company-wide: doctors per office id (for dynamic doctor list when office changes) */
  doctorsByOfficeId?: Record<string, Doctor[]>;
  /** Pre-signed profile picture URLs by doctor id (for embed; avoids auth-required API) */
  profilePictureUrls?: Record<string, string>;
  /** Custom notice rendered inside the X-ray uploader. Empty/undefined/whitespace falls back to the default "Please submit radiographs as JPEG or PNG files." */
  xrayUploadMessage?: string;
  /** When false, the embed form hides the dentist picker and routes referrals to the office's default doctor. Default: true. */
  showDoctors?: boolean;
  /** When false, doctor profile pictures are hidden on office/provider location cards in the embed form. Default: true (#1402). */
  showDoctorImages?: boolean;
  /** When true, the embed form uses the alternative 3-step layout (Demographic → Referring → Procedures). Default: false. */
  alternateStepLayout?: boolean;
  /** Optional Google Tag Manager container ID (GTM-XXXX) for embed analytics */
  gtmContainerId?: string;
  /**
   * Office-wide aggregate of doctor skill names (deduped, sorted) — mirrors the GD Web
   * Form's "What we are known for!" section. See aggregateOfficeDoctorSkillNames.
   */
  skillSetNames?: string[];
  /** Badge style for `skillSetNames` — mirrors the GD Web Form's `knownForStyle` setting.
   * `'pill'` (default) is a single plain purple pill; `'colorful'` mixes shapes/colors. */
  knownForStyle?: 'pill' | 'colorful';
  /**
   * Unsaved enable/disable edits to referral reasons, from the office-embed builder's own
   * preview override (never persisted here — the real toggle lives in the database and is
   * fetched separately). Applied on top of whatever officeReasonOptions the form fetched, so
   * an admin sees a reason appear/disappear immediately, before clicking that builder's Save.
   * #see EmbedReferralBuilder.tsx, applyReasonOverrides in utils/embed-reason-overrides.ts.
   */
  reasonOverrides?: EmbedReasonOverride[];
  /**
   * Unsaved reorder edits to referral reasons, from the office-embed builder's own preview
   * override — specialty -> ordered list of reason `value`s (not DB ids; the public embed API's
   * SpecialtyReason shape has no id). Applied on top of officeReasonOptions for the currently
   * selected specialty only. #see EmbedReferralBuilder.tsx, applyReasonOverrides.
   */
  reasonOrderOverrides?: Record<string, string[]>;
  /**
   * Bumped by the embed builder after a reasons-editor mutation that already persisted
   * (add/rename/delete/procedures) — has no meaning itself, just forces
   * ReferralDetailsSection's officeReasonOptions fetch to re-run in place, without reloading the
   * iframe (which would otherwise reset doctor/specialty selection and the current step).
   */
  reasonsRefreshToken?: number;
  /** When true, an office-location Google Map renders on the embed form. Default: false. */
  showMap?: boolean;
  /** When false, hides the "Send a Secure Link to the Patient" email-insurance option on the
   * embed form's Patient Insurance section. Default: true. */
  showInsuranceEmailOption?: boolean;
  /** When false, hides the "Upload Information Manually" staff-upload option on the embed
   * form's Patient Insurance section. Default: true. */
  showInsuranceUploadOption?: boolean;
  /**
   * Geocoded office + Google Maps config for the map, present whenever an API key is
   * configured — independent of `showMap` so the builder preview can toggle visibility
   * instantly with no save round trip (see EmbedOfficeMapConfig). Render only when both
   * `showMap` and `map` are present, mirroring the Contact Us (GD Web Form) pattern.
   */
  map?: EmbedOfficeMapConfig | null;
}

/** Office location + Google Maps config for the optional map shown on an embed form. Shared
 * shape between the Contact Us (GD Web Form) and specialist Referral Web Form embeds. */
export interface EmbedOfficeMapConfig {
  apiKey: string;
  mapId: string;
  office: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    streetAddress: string;
    city: string;
    state: string;
    zip: string;
    ste: string | null;
    googleRating?: { name: string | null; rating: number; reviewCount: number } | null;
  };
}

/** One pending, unsaved referral-reason enable/disable edit — see EmbedContext.reasonOverrides. */
export interface EmbedReasonOverride {
  specialty: string;
  value: string;
  label: string;
  procedures?: ProcedureGroup[] | null;
  isEnabled: boolean;
}

/** Embed wizard "Insurance Display Mode" — controls which carriers appear above the form. */
export type InsuranceDisplayMode = 'ALL' | 'IN_NETWORK_ONLY' | 'OUT_OF_NETWORK_ONLY' | 'HIDE';

export const INSURANCE_DISPLAY_MODES: readonly InsuranceDisplayMode[] = [
  'ALL',
  'IN_NETWORK_ONLY',
  'OUT_OF_NETWORK_ONLY',
  'HIDE',
] as const;

export const DEFAULT_INSURANCE_DISPLAY_MODE: InsuranceDisplayMode = 'ALL';
